import React, { useState, useEffect, useCallback } from 'react';
import {
    Mail, Settings, Send, FileText, BarChart2, RefreshCw,
    Plus, Play, Pause, Copy, Trash2, Edit, Eye, Zap,
    CheckCircle, XCircle, AlertTriangle, UserX, RotateCw, AlertCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
    emailApi,
    type EmailFlow,
    type EmailTemplate,
    type ProviderInfo,
    type EmailAnalytics,
    type EventType,
    type EmailConfig
} from '../../lib/api/email';
import { TemplatePreviewModal, RichTextEditor } from '../../components/email';


// ==================== SUB-COMPONENTS ====================

interface TabButtonProps {
    label: string;
    active: boolean;
    onClick: () => void;
    icon?: React.ElementType;
    badge?: number;
}

const TabButton: React.FC<TabButtonProps> = ({ label, active, onClick, icon: Icon, badge }) => (
    <button
        onClick={onClick}
        className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${active
            ? 'bg-neutral-900 text-white'
            : 'text-neutral-600 hover:bg-neutral-100'
            }`}
    >
        {Icon && <Icon size={14} />}
        {label}
        {badge !== undefined && badge > 0 && (
            <span className={`text-xs px-1.5 py-0.5 rounded-full ${active ? 'bg-white/20 text-white' : 'bg-neutral-200 text-neutral-600'
                }`}>
                {badge}
            </span>
        )}
    </button>
);

interface MiniStatProps {
    label: string;
    value: number | string;
    icon: React.ElementType;
    color: string;
}

const MiniStat: React.FC<MiniStatProps> = ({ label, value, icon: Icon, color }) => (
    <div className="bg-white border border-neutral-200 rounded-lg p-4 flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
            <Icon size={18} />
        </div>
        <div>
            <div className="text-xl font-bold text-neutral-900">{value}</div>
            <div className="text-xs text-neutral-500">{label}</div>
        </div>
    </div>
);

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
    const colors: Record<string, string> = {
        active: 'bg-emerald-100 text-emerald-700',
        draft: 'bg-neutral-100 text-neutral-600',
        paused: 'bg-amber-100 text-amber-700',
        archived: 'bg-neutral-200 text-neutral-500'
    };
    return (
        <span className={`text-xs px-2 py-1 rounded-full font-medium ${colors[status] || colors.draft}`}>
            {status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
    );
};

// Failed Executions Tab Component
interface FailedExecutionsTabProps {
    executions: Array<{
        id: string;
        email: string;
        flowName: string;
        stepName: string;
        errorMessage?: string;
        failedAt?: string;
        scheduledFor: string;
        createdAt: string;
    }>;
    pagination: { page: number; total: number; pages: number };
    onLoadData: (page: number) => void;
    onRetry: (id: string) => void;
}

const FailedExecutionsTab: React.FC<FailedExecutionsTabProps> = ({ executions, pagination, onLoadData, onRetry }) => {
    React.useEffect(() => {
        onLoadData(1);
    }, []);

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <AlertCircle size={18} className="text-red-500" />
                    <span className="text-sm text-neutral-600">{pagination.total} failed executions</span>
                </div>
            </div>

            {executions.length > 0 ? (
                <div className="bg-neutral-50 border border-neutral-100 rounded-xl overflow-hidden">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-neutral-200 bg-neutral-100">
                                <th className="text-left py-3 px-4 text-xs font-semibold text-neutral-600 uppercase">Email</th>
                                <th className="text-left py-3 px-4 text-xs font-semibold text-neutral-600 uppercase">Flow</th>
                                <th className="text-left py-3 px-4 text-xs font-semibold text-neutral-600 uppercase">Step</th>
                                <th className="text-left py-3 px-4 text-xs font-semibold text-neutral-600 uppercase">Error</th>
                                <th className="text-left py-3 px-4 text-xs font-semibold text-neutral-600 uppercase">Failed At</th>
                                <th className="text-center py-3 px-4 text-xs font-semibold text-neutral-600 uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {executions.map(exec => (
                                <tr key={exec.id} className="border-b border-neutral-100 hover:bg-neutral-50">
                                    <td className="py-3 px-4 text-sm text-neutral-900">{exec.email}</td>
                                    <td className="py-3 px-4 text-sm text-neutral-600">{exec.flowName}</td>
                                    <td className="py-3 px-4 text-sm text-neutral-600">{exec.stepName}</td>
                                    <td className="py-3 px-4">
                                        <span className="text-xs text-red-600 bg-red-50 px-2 py-1 rounded">
                                            {exec.errorMessage || 'Unknown error'}
                                        </span>
                                    </td>
                                    <td className="py-3 px-4 text-xs text-neutral-500">
                                        {exec.failedAt ? new Date(exec.failedAt).toLocaleString() : '-'}
                                    </td>
                                    <td className="py-3 px-4 text-center">
                                        <button
                                            onClick={() => onRetry(exec.id)}
                                            className="px-3 py-1.5 text-xs bg-neutral-900 text-white rounded-lg hover:bg-neutral-800 flex items-center gap-1 mx-auto"
                                        >
                                            <RotateCw size={12} />
                                            Retry
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="text-center py-16 bg-neutral-50 rounded-xl border border-neutral-100">
                    <CheckCircle size={48} className="mx-auto mb-4 text-emerald-400" />
                    <h3 className="text-lg font-medium text-neutral-900 mb-2">No Failed Emails</h3>
                    <p className="text-sm text-neutral-500">All email executions are running smoothly</p>
                </div>
            )}

            {/* Pagination */}
            {pagination.pages > 1 && (
                <div className="flex items-center justify-center gap-2">
                    <button
                        onClick={() => onLoadData(pagination.page - 1)}
                        disabled={pagination.page <= 1}
                        className="px-3 py-1.5 text-xs border border-neutral-200 rounded-lg disabled:opacity-50"
                    >
                        Previous
                    </button>
                    <span className="text-xs text-neutral-500">
                        Page {pagination.page} of {pagination.pages}
                    </span>
                    <button
                        onClick={() => onLoadData(pagination.page + 1)}
                        disabled={pagination.page >= pagination.pages}
                        className="px-3 py-1.5 text-xs border border-neutral-200 rounded-lg disabled:opacity-50"
                    >
                        Next
                    </button>
                </div>
            )}
        </div>
    );
};

// Unsubscribed Emails Tab Component
interface UnsubscribedTabProps {
    emails: Array<{
        id: string;
        email: string;
        reason?: string;
        createdAt: string;
    }>;
    pagination: { page: number; total: number; pages: number };
    onLoadData: (page: number) => void;
    onResubscribe: (email: string) => void;
}

const UnsubscribedTab: React.FC<UnsubscribedTabProps> = ({ emails, pagination, onLoadData, onResubscribe }) => {
    React.useEffect(() => {
        onLoadData(1);
    }, []);

    const reasonLabels: Record<string, string> = {
        too_many: 'Too many emails',
        not_relevant: 'Content not relevant',
        never_signed_up: 'Never signed up',
        other: 'Other'
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <UserX size={18} className="text-neutral-500" />
                    <span className="text-sm text-neutral-600">{pagination.total} unsubscribed emails</span>
                </div>
            </div>

            {emails.length > 0 ? (
                <div className="bg-neutral-50 border border-neutral-100 rounded-xl overflow-hidden">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-neutral-200 bg-neutral-100">
                                <th className="text-left py-3 px-4 text-xs font-semibold text-neutral-600 uppercase">Email</th>
                                <th className="text-left py-3 px-4 text-xs font-semibold text-neutral-600 uppercase">Reason</th>
                                <th className="text-left py-3 px-4 text-xs font-semibold text-neutral-600 uppercase">Unsubscribed At</th>
                                <th className="text-center py-3 px-4 text-xs font-semibold text-neutral-600 uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {emails.map(item => (
                                <tr key={item.id} className="border-b border-neutral-100 hover:bg-neutral-50">
                                    <td className="py-3 px-4 text-sm text-neutral-900">{item.email}</td>
                                    <td className="py-3 px-4">
                                        <span className="text-xs text-neutral-600 bg-neutral-100 px-2 py-1 rounded">
                                            {item.reason ? (reasonLabels[item.reason] || item.reason) : 'Not specified'}
                                        </span>
                                    </td>
                                    <td className="py-3 px-4 text-xs text-neutral-500">
                                        {new Date(item.createdAt).toLocaleString()}
                                    </td>
                                    <td className="py-3 px-4 text-center">
                                        <button
                                            onClick={() => onResubscribe(item.email)}
                                            className="px-3 py-1.5 text-xs bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center gap-1 mx-auto"
                                        >
                                            <Mail size={12} />
                                            Resubscribe
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="text-center py-16 bg-neutral-50 rounded-xl border border-neutral-100">
                    <Mail size={48} className="mx-auto mb-4 text-emerald-400" />
                    <h3 className="text-lg font-medium text-neutral-900 mb-2">No Unsubscribed Users</h3>
                    <p className="text-sm text-neutral-500">Everyone is still receiving your emails</p>
                </div>
            )}

            {/* Pagination */}
            {pagination.pages > 1 && (
                <div className="flex items-center justify-center gap-2">
                    <button
                        onClick={() => onLoadData(pagination.page - 1)}
                        disabled={pagination.page <= 1}
                        className="px-3 py-1.5 text-xs border border-neutral-200 rounded-lg disabled:opacity-50"
                    >
                        Previous
                    </button>
                    <span className="text-xs text-neutral-500">
                        Page {pagination.page} of {pagination.pages}
                    </span>
                    <button
                        onClick={() => onLoadData(pagination.page + 1)}
                        disabled={pagination.page >= pagination.pages}
                        className="px-3 py-1.5 text-xs border border-neutral-200 rounded-lg disabled:opacity-50"
                    >
                        Next
                    </button>
                </div>
            )}
        </div>
    );
};

// ==================== MAIN COMPONENT ====================

type TabType = 'overview' | 'system' | 'workflows' | 'templates' | 'failed' | 'unsubscribed' | 'settings';

interface FailedExecution {
    id: string;
    email: string;
    flowName: string;
    stepName: string;
    errorMessage?: string;
    failedAt?: string;
    scheduledFor: string;
    createdAt: string;
}

interface UnsubscribedEmail {
    id: string;
    email: string;
    reason?: string;
    createdAt: string;
}

export const EmailManagerPage: React.FC = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<TabType>('overview');
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    // Data states
    const [flows, setFlows] = useState<EmailFlow[]>([]);
    const [templates, setTemplates] = useState<EmailTemplate[]>([]);
    const [providerInfo, setProviderInfo] = useState<ProviderInfo | null>(null);
    const [analytics, setAnalytics] = useState<EmailAnalytics | null>(null);
    const [eventTypes, setEventTypes] = useState<EventType[]>([]);
    const [configs, setConfigs] = useState<EmailConfig[]>([]);
    const [failedExecutions, setFailedExecutions] = useState<FailedExecution[]>([]);
    const [failedPagination, setFailedPagination] = useState({ page: 1, total: 0, pages: 0 });
    const [unsubscribedEmails, setUnsubscribedEmails] = useState<UnsubscribedEmail[]>([]);
    const [unsubPagination, setUnsubPagination] = useState({ page: 1, total: 0, pages: 0 });

    // Modal states
    const [showCreateFlow, setShowCreateFlow] = useState(false);
    const [showCreateTemplate, setShowCreateTemplate] = useState(false);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [previewTemplate, setPreviewTemplate] = useState<EmailTemplate | null>(null);
    const [testEmail, setTestEmail] = useState('');
    const [testEmailStatus, setTestEmailStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

    // New flow form
    const [newFlowName, setNewFlowName] = useState('');
    const [newFlowTrigger, setNewFlowTrigger] = useState('');
    const [newFlowDescription, setNewFlowDescription] = useState('');

    // New template form
    const [newTemplateName, setNewTemplateName] = useState('');
    const [newTemplateSubject, setNewTemplateSubject] = useState('');
    const [newTemplateContent, setNewTemplateContent] = useState('');


    const fetchData = useCallback(async () => {
        try {
            const [flowsData, templatesData, providerData, analyticsData, eventTypesData, configsData] = await Promise.all([
                emailApi.listFlows().catch(() => []),
                emailApi.listTemplates().catch(() => []),
                emailApi.getProviderInfo().catch(() => null),
                emailApi.getAnalytics().catch(() => null),
                emailApi.getEventTypes().catch(() => []),
                emailApi.getConfig().catch(() => [])
            ]);

            setFlows(flowsData);
            setTemplates(templatesData);
            setProviderInfo(providerData);
            setAnalytics(analyticsData);
            setEventTypes(eventTypesData);
            setConfigs(configsData);
        } catch (error) {
            console.error('Failed to fetch email data:', error);
        }
    }, []);

    const handleRefresh = async () => {
        setRefreshing(true);
        await fetchData();
        setRefreshing(false);
    };

    useEffect(() => {
        setLoading(true);
        fetchData().finally(() => setLoading(false));
    }, [fetchData]);

    const handleTestEmail = async () => {
        if (!testEmail) return;
        setTestEmailStatus('sending');
        try {
            await emailApi.testProvider(testEmail);
            setTestEmailStatus('success');
            setTimeout(() => setTestEmailStatus('idle'), 3000);
        } catch {
            setTestEmailStatus('error');
            setTimeout(() => setTestEmailStatus('idle'), 3000);
        }
    };

    const handleCreateFlow = async () => {
        if (!newFlowName || !newFlowTrigger) return;
        try {
            const flow = await emailApi.createFlow({
                name: newFlowName,
                description: newFlowDescription,
                triggerType: newFlowTrigger
            });
            setShowCreateFlow(false);
            setNewFlowName('');
            setNewFlowTrigger('');
            setNewFlowDescription('');
            navigate(`/email/flows/${flow.id}`);
        } catch (error) {
            console.error('Failed to create flow:', error);
        }
    };

    const handleCreateTemplate = async () => {
        if (!newTemplateName || !newTemplateSubject || !newTemplateContent) return;
        try {
            await emailApi.createTemplate({
                name: newTemplateName,
                subject: newTemplateSubject,
                htmlContent: newTemplateContent
            });
            setShowCreateTemplate(false);
            setNewTemplateName('');
            setNewTemplateSubject('');
            setNewTemplateContent('');
            fetchData();
        } catch (error) {
            console.error('Failed to create template:', error);
        }
    };

    const handleToggleFlowStatus = async (flow: EmailFlow) => {
        try {
            if (flow.status === 'active') {
                await emailApi.pauseFlow(flow.id);
            } else {
                await emailApi.activateFlow(flow.id);
            }
            fetchData();
        } catch (error) {
            console.error('Failed to toggle flow status:', error);
        }
    };

    const handleDeleteFlow = async (flowId: string) => {
        if (!confirm('Are you sure you want to delete this flow?')) return;
        try {
            await emailApi.deleteFlow(flowId);
            fetchData();
        } catch (error) {
            console.error('Failed to delete flow:', error);
        }
    };

    const handleDuplicateFlow = async (flowId: string) => {
        try {
            await emailApi.duplicateFlow(flowId);
            fetchData();
        } catch (error) {
            console.error('Failed to duplicate flow:', error);
        }
    };

    const handleDeleteTemplate = async (templateId: string) => {
        if (!confirm('Are you sure you want to delete this template?')) return;
        try {
            await emailApi.deleteTemplate(templateId);
            fetchData();
        } catch (error) {
            console.error('Failed to delete template:', error);
        }
    };

    const handleResetDefaults = async () => {
        try {
            setLoading(true);
            await emailApi.resetDefaults();
            await fetchData();
            setShowResetModal(false);
        } catch (error) {
            console.error('Failed to reset defaults:', error);
        } finally {
            setLoading(false);
        }
    };

    const [showResetModal, setShowResetModal] = useState(false);

    const ConfirmResetModal = () => {
        if (!showResetModal) return null;

        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 m-4 animate-in fade-in zoom-in-95 duration-200">
                    <div className="flex items-center gap-3 text-amber-600 mb-4">
                        <div className="p-2 bg-amber-100 rounded-lg">
                            <AlertTriangle size={24} />
                        </div>
                        <h3 className="text-lg font-semibold text-neutral-900">Reset Email Defaults</h3>
                    </div>

                    <p className="text-neutral-600 mb-6">
                        This will force a re-seed of all default email templates and system workflows.
                        Any customizations made to default templates will be preserved unless they conflict with critical updates.
                        Are you sure you want to continue?
                    </p>

                    <div className="flex items-center justify-end gap-3">
                        <button
                            onClick={() => setShowResetModal(false)}
                            className="px-4 py-2 text-sm font-medium text-neutral-600 hover:bg-neutral-100 rounded-lg transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleResetDefaults}
                            className="px-4 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg transition-colors flex items-center gap-2"
                        >
                            <RefreshCw size={16} />
                            Reset Defaults
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="h-full flex flex-col p-1">
            <div className="flex-1 bg-white border border-neutral-200 rounded-lg overflow-hidden flex flex-col shadow-sm">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-neutral-200">
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                            <Mail size={20} className="text-neutral-600" />
                            <h1 className="text-lg font-semibold text-neutral-900">Email Manager</h1>
                        </div>
                        <div className="flex items-center gap-1 bg-neutral-100 rounded-lg p-1">
                            <TabButton label="Overview" active={activeTab === 'overview'} onClick={() => setActiveTab('overview')} icon={BarChart2} />
                            <TabButton label="System" active={activeTab === 'system'} onClick={() => setActiveTab('system')} icon={Settings} badge={flows.filter(f => f.isSystem).length} />
                            <TabButton label="Workflows" active={activeTab === 'workflows'} onClick={() => setActiveTab('workflows')} icon={Zap} badge={flows.filter(f => !f.isSystem).length} />
                            <TabButton label="Templates" active={activeTab === 'templates'} onClick={() => setActiveTab('templates')} icon={FileText} badge={templates.length} />
                            <TabButton label="Failed" active={activeTab === 'failed'} onClick={() => setActiveTab('failed')} icon={AlertCircle} />
                            <TabButton label="Unsubscribed" active={activeTab === 'unsubscribed'} onClick={() => setActiveTab('unsubscribed')} icon={UserX} />
                            <TabButton label="Settings" active={activeTab === 'settings'} onClick={() => setActiveTab('settings')} icon={Settings} />
                        </div>
                    </div>
                    <button
                        onClick={() => setShowResetModal(true)}
                        className="px-3 py-2 text-xs bg-white border border-neutral-200 text-neutral-600 rounded-lg hover:bg-neutral-50 transition-colors flex items-center gap-1"
                    >
                        <RefreshCw size={12} />
                        Reset
                    </button>
                    <button
                        onClick={handleRefresh}
                        disabled={refreshing}
                        className="px-3 py-2 text-xs bg-neutral-900 text-white rounded-lg hover:bg-neutral-800 transition-colors flex items-center gap-1"
                    >
                        <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} />
                        Refresh
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4">
                    {loading ? (
                        <div className="space-y-4">
                            <div className="grid grid-cols-4 gap-4">
                                {[1, 2, 3, 4].map(i => (
                                    <div key={i} className="h-24 bg-neutral-100 rounded-lg animate-pulse" />
                                ))}
                            </div>
                            <div className="h-64 bg-neutral-100 rounded-lg animate-pulse" />
                        </div>
                    ) : (
                        <>
                            {/* OVERVIEW TAB */}
                            {activeTab === 'overview' && (
                                <div className="space-y-6">
                                    {/* Quick Stats */}
                                    <div className="grid grid-cols-4 gap-4">
                                        <MiniStat label="Active Flows" value={analytics?.overview.activeFlows || 0} icon={Zap} color="bg-emerald-100 text-emerald-600" />
                                        <MiniStat label="Total Sent" value={analytics?.overview.totalSent?.toLocaleString() || 0} icon={Send} color="bg-blue-100 text-blue-600" />
                                        <MiniStat label="Open Rate" value={`${analytics?.overview.overallOpenRate || 0}%`} icon={Eye} color="bg-purple-100 text-purple-600" />
                                        <MiniStat label="Click Rate" value={`${analytics?.overview.overallClickRate || 0}%`} icon={CheckCircle} color="bg-amber-100 text-amber-600" />
                                    </div>

                                    {/* Provider Status */}
                                    <div className="bg-neutral-50 border border-neutral-100 rounded-xl p-5">
                                        <h3 className="text-sm font-semibold text-neutral-900 mb-4">Email Provider Status</h3>
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                {providerInfo?.isConfigured ? (
                                                    <CheckCircle size={20} className="text-emerald-500" />
                                                ) : (
                                                    <AlertTriangle size={20} className="text-amber-500" />
                                                )}
                                                <div>
                                                    <div className="font-medium text-neutral-900">{providerInfo?.currentProvider || 'Not Configured'}</div>
                                                    <div className="text-xs text-neutral-500">From: {providerInfo?.fromEmail || 'N/A'}</div>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => setActiveTab('settings')}
                                                className="px-3 py-1.5 text-xs border border-neutral-200 rounded-lg hover:bg-neutral-100"
                                            >
                                                Configure
                                            </button>
                                        </div>
                                    </div>

                                    {/* Recent Flows */}
                                    <div className="bg-neutral-50 border border-neutral-100 rounded-xl p-5">
                                        <div className="flex items-center justify-between mb-4">
                                            <h3 className="text-sm font-semibold text-neutral-900">Email Flows</h3>
                                            <button
                                                onClick={() => setShowCreateFlow(true)}
                                                className="px-3 py-1.5 text-xs bg-neutral-900 text-white rounded-lg hover:bg-neutral-800 flex items-center gap-1"
                                            >
                                                <Plus size={12} />
                                                Create Flow
                                            </button>
                                        </div>
                                        {flows.length > 0 ? (
                                            <div className="space-y-2">
                                                {flows.slice(0, 5).map(flow => (
                                                    <div
                                                        key={flow.id}
                                                        className="bg-white border border-neutral-200 rounded-lg p-4 flex items-center justify-between hover:border-neutral-300 cursor-pointer transition-colors"
                                                        onClick={() => navigate(`/email/flows/${flow.id}`)}
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <Mail size={16} className="text-neutral-400" />
                                                            <div>
                                                                <div className="font-medium text-neutral-900">{flow.name}</div>
                                                                <div className="text-xs text-neutral-500">
                                                                    {flow.triggerType} • {flow.stepsCount || flow.steps?.length || 0} steps
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-3">
                                                            <div className="text-xs text-neutral-500">
                                                                {flow.totalSent?.toLocaleString() || 0} sent
                                                            </div>
                                                            <StatusBadge status={flow.status} />
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="text-center py-8 text-neutral-400">
                                                <Zap size={32} className="mx-auto mb-2" />
                                                <p className="text-sm">No email flows yet</p>
                                                <p className="text-xs mt-1">Create your first automated email flow</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* SYSTEM TAB */}
                            {activeTab === 'system' && (
                                <div className="space-y-4">


                                    {/* System Flows Section */}
                                    <div className="space-y-4">
                                        <div className="flex items-center gap-2">
                                            <div className="bg-purple-100 text-purple-700 p-1.5 rounded-lg">
                                                <Settings size={16} />
                                            </div>
                                            <div>
                                                <h3 className="font-semibold text-neutral-900">System Emails</h3>
                                                <p className="text-xs text-neutral-500">Permanent transactional emails (Verification, Password Reset, etc.)</p>
                                            </div>
                                        </div>

                                        <div className="bg-neutral-50 border border-neutral-100 rounded-xl overflow-hidden">
                                            <table className="w-full">
                                                <thead>
                                                    <tr className="border-b border-neutral-200 bg-neutral-100">
                                                        <th className="text-left py-3 px-4 text-xs font-semibold text-neutral-600 uppercase">Email Type</th>
                                                        <th className="text-left py-3 px-4 text-xs font-semibold text-neutral-600 uppercase">Trigger</th>
                                                        <th className="text-left py-3 px-4 text-xs font-semibold text-neutral-600 uppercase">Stats (Sent / Open)</th>
                                                        <th className="text-center py-3 px-4 text-xs font-semibold text-neutral-600 uppercase">Status</th>
                                                        <th className="text-center py-3 px-4 text-xs font-semibold text-neutral-600 uppercase">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {flows.filter(f => f.isSystem).map(flow => (
                                                        <tr key={flow.id} className="border-b border-neutral-100 hover:bg-neutral-50">
                                                            <td className="py-3 px-4">
                                                                <button
                                                                    onClick={() => navigate(`/email/flows/${flow.id}`)}
                                                                    className="font-medium text-sm text-neutral-900 hover:text-blue-600 flex items-center gap-2"
                                                                >
                                                                    {flow.name}
                                                                </button>
                                                                <div className="text-xs text-neutral-500 mt-0.5">{flow.description}</div>
                                                            </td>
                                                            <td className="py-3 px-4">
                                                                <span className="text-xs font-mono text-neutral-500">
                                                                    {flow.triggerType}
                                                                </span>
                                                            </td>
                                                            <td className="py-3 px-4">
                                                                <div className="flex items-center gap-3 text-sm text-neutral-600">
                                                                    <span>{flow.totalSent?.toLocaleString() || 0} sent</span>
                                                                    <span className="text-neutral-300">|</span>
                                                                    <span>{flow.openRate || 0}% open</span>
                                                                </div>
                                                            </td>
                                                            <td className="py-3 px-4 text-center">
                                                                <StatusBadge status={flow.status} />
                                                            </td>
                                                            <td className="py-3 px-4">
                                                                <div className="flex items-center justify-center gap-1">
                                                                    <button
                                                                        onClick={() => navigate(`/email/flows/${flow.id}`)}
                                                                        className="px-3 py-1.5 bg-white border border-neutral-200 text-neutral-700 text-xs rounded-lg hover:bg-neutral-50 flex items-center gap-1 shadow-sm"
                                                                    >
                                                                        <Edit size={12} />
                                                                        Edit Content
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* WORKFLOWS TAB */}
                            {activeTab === 'workflows' && (
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <div className="bg-blue-100 text-blue-700 p-1.5 rounded-lg">
                                                <Zap size={16} />
                                            </div>
                                            <div>
                                                <h3 className="font-semibold text-neutral-900">Marketing Workflows</h3>
                                                <p className="text-xs text-neutral-500">Automated sequences for user engagement</p>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => setShowCreateFlow(true)}
                                            className="px-3 py-2 text-xs bg-neutral-900 text-white rounded-lg hover:bg-neutral-800 flex items-center gap-1"
                                        >
                                            <Plus size={12} />
                                            Create Workflow
                                        </button>
                                    </div>

                                    <div className="bg-neutral-50 border border-neutral-100 rounded-xl overflow-hidden">
                                        {flows.filter(f => !f.isSystem).length > 0 ? (
                                            <table className="w-full">
                                                <thead>
                                                    <tr className="border-b border-neutral-200 bg-neutral-100">
                                                        <th className="text-left py-3 px-4 text-xs font-semibold text-neutral-600 uppercase">Flow Name</th>
                                                        <th className="text-left py-3 px-4 text-xs font-semibold text-neutral-600 uppercase">Trigger</th>
                                                        <th className="text-left py-3 px-4 text-xs font-semibold text-neutral-600 uppercase">Steps</th>
                                                        <th className="text-left py-3 px-4 text-xs font-semibold text-neutral-600 uppercase">Stats</th>
                                                        <th className="text-center py-3 px-4 text-xs font-semibold text-neutral-600 uppercase">Status</th>
                                                        <th className="text-center py-3 px-4 text-xs font-semibold text-neutral-600 uppercase">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {flows.filter(f => !f.isSystem).map(flow => (
                                                        <tr key={flow.id} className="border-b border-neutral-100 hover:bg-neutral-50">
                                                            <td className="py-3 px-4">
                                                                <button
                                                                    onClick={() => navigate(`/email/flows/${flow.id}`)}
                                                                    className="font-medium text-sm text-neutral-900 hover:text-blue-600"
                                                                >
                                                                    {flow.name}
                                                                </button>
                                                                {flow.description && (
                                                                    <div className="text-xs text-neutral-500 mt-0.5">{flow.description}</div>
                                                                )}
                                                            </td>
                                                            <td className="py-3 px-4">
                                                                <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700 font-medium">
                                                                    {flow.triggerType}
                                                                </span>
                                                            </td>
                                                            <td className="py-3 px-4 text-sm text-neutral-600">
                                                                {flow.stepsCount || flow.steps?.length || 0} step(s)
                                                            </td>
                                                            <td className="py-3 px-4">
                                                                <div className="flex items-center gap-2 text-xs text-neutral-600">
                                                                    <span>{flow.totalSent?.toLocaleString() || 0} sent</span>
                                                                    <span className="text-neutral-300">|</span>
                                                                    <span>{flow.openRate || 0}% open</span>
                                                                </div>
                                                            </td>
                                                            <td className="py-3 px-4 text-center">
                                                                <StatusBadge status={flow.status} />
                                                            </td>
                                                            <td className="py-3 px-4">
                                                                <div className="flex items-center justify-center gap-1">
                                                                    <button
                                                                        onClick={() => handleToggleFlowStatus(flow)}
                                                                        className="p-1.5 hover:bg-neutral-200 rounded"
                                                                        title={flow.status === 'active' ? 'Pause' : 'Activate'}
                                                                    >
                                                                        {flow.status === 'active' ? <Pause size={14} /> : <Play size={14} />}
                                                                    </button>
                                                                    <button
                                                                        onClick={() => navigate(`/email/flows/${flow.id}`)}
                                                                        className="p-1.5 hover:bg-neutral-200 rounded"
                                                                        title="Edit"
                                                                    >
                                                                        <Edit size={14} />
                                                                    </button>
                                                                    <button
                                                                        onClick={() => handleDuplicateFlow(flow.id)}
                                                                        className="p-1.5 hover:bg-neutral-200 rounded"
                                                                        title="Duplicate"
                                                                    >
                                                                        <Copy size={14} />
                                                                    </button>
                                                                    <button
                                                                        onClick={() => handleDeleteFlow(flow.id)}
                                                                        className="p-1.5 hover:bg-red-100 text-red-600 rounded"
                                                                        title="Delete"
                                                                    >
                                                                        <Trash2 size={14} />
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        ) : (
                                            <div className="text-center py-12 text-neutral-400">
                                                <Zap size={32} className="mx-auto mb-2 opacity-50" />
                                                <p className="text-sm">No marketing workflows yet</p>
                                                <button
                                                    onClick={() => setShowCreateFlow(true)}
                                                    className="mt-3 text-xs text-blue-600 hover:text-blue-800 font-medium"
                                                >
                                                    + Create your first workflow
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* TEMPLATES TAB */}
                            {activeTab === 'templates' && (
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-neutral-600">{templates.length} email templates</span>
                                        <button
                                            onClick={() => setShowCreateTemplate(true)}
                                            className="px-3 py-2 text-xs bg-neutral-900 text-white rounded-lg hover:bg-neutral-800 flex items-center gap-1"
                                        >
                                            <Plus size={12} />
                                            Create Template
                                        </button>
                                    </div>

                                    <div className="grid grid-cols-3 gap-4">
                                        {templates.length > 0 ? (
                                            templates.map(template => (
                                                <div key={template.id} className="bg-white border border-neutral-200 rounded-xl p-4 hover:border-neutral-300 transition-colors">
                                                    <div className="flex items-start justify-between mb-3">
                                                        <div>
                                                            <div className="font-medium text-neutral-900">{template.name}</div>
                                                            <div className="text-xs text-neutral-500 mt-0.5">{template.slug}</div>
                                                        </div>
                                                        <span className={`text-xs px-2 py-0.5 rounded-full ${template.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-neutral-100 text-neutral-500'
                                                            }`}>
                                                            {template.isActive ? 'Active' : 'Inactive'}
                                                        </span>
                                                    </div>
                                                    <div className="text-sm text-neutral-600 mb-3">{template.subject}</div>
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-xs text-neutral-400 capitalize">{template.category}</span>
                                                        <div className="flex items-center gap-1">
                                                            <button
                                                                onClick={() => {
                                                                    setPreviewTemplate(template);
                                                                    setShowPreviewModal(true);
                                                                }}
                                                                className="p-1.5 hover:bg-neutral-100 rounded"
                                                                title="Preview"
                                                            >
                                                                <Eye size={14} />
                                                            </button>
                                                            <button className="p-1.5 hover:bg-neutral-100 rounded" title="Edit">
                                                                <Edit size={14} />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteTemplate(template.id)}
                                                                className="p-1.5 hover:bg-red-100 rounded text-red-600"
                                                                title="Delete"
                                                            >
                                                                <Trash2 size={14} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="col-span-3 py-16 text-center bg-gradient-to-br from-neutral-50 to-neutral-100 rounded-xl border border-neutral-200">
                                                <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-4">
                                                    <FileText size={28} className="text-purple-500" />
                                                </div>
                                                <h3 className="text-lg font-medium text-neutral-900 mb-2">No Email Templates Yet</h3>
                                                <p className="text-sm text-neutral-500 mb-6 max-w-sm mx-auto">
                                                    Create reusable email templates for your flows. Templates make it easy to maintain consistent branding.
                                                </p>
                                                <button
                                                    onClick={() => setShowCreateTemplate(true)}
                                                    className="px-4 py-2 bg-neutral-900 text-white rounded-lg hover:bg-neutral-800 text-sm font-medium inline-flex items-center gap-2"
                                                >
                                                    <Plus size={16} />
                                                    Create Your First Template
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* SETTINGS TAB */}
                            {activeTab === 'settings' && (
                                <div className="space-y-6 max-w-2xl">
                                    {/* Provider Settings */}
                                    <div className="bg-neutral-50 border border-neutral-100 rounded-xl p-5">
                                        <h3 className="text-sm font-semibold text-neutral-900 mb-4">Email Provider</h3>
                                        <div className="space-y-4">
                                            {/* Provider Status Card */}
                                            <div className="flex items-center justify-between p-4 bg-white border border-neutral-200 rounded-lg">
                                                <div className="flex items-center gap-3">
                                                    {providerInfo?.isConfigured ? (
                                                        <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                                                            <CheckCircle size={20} className="text-emerald-600" />
                                                        </div>
                                                    ) : (
                                                        <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                                                            <AlertTriangle size={20} className="text-amber-600" />
                                                        </div>
                                                    )}
                                                    <div>
                                                        <div className="font-medium text-neutral-900">
                                                            {providerInfo?.currentProvider || 'Not Configured'}
                                                        </div>
                                                        <div className="text-xs text-neutral-500">
                                                            {providerInfo?.isConfigured ? 'Provider is ready' : 'Configure your provider'}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Provider Selection */}
                                            <div className="space-y-2">
                                                <label className="text-xs font-bold text-neutral-900 uppercase tracking-wide">Select Provider</label>
                                                <select
                                                    value={configs.find(c => c.key === 'EMAIL_PROVIDER')?.value || ''}
                                                    onChange={async (e) => {
                                                        const value = e.target.value;
                                                        try {
                                                            await emailApi.updateConfig([{
                                                                key: 'EMAIL_PROVIDER',
                                                                value: value,
                                                                description: 'Selected Email Provider'
                                                            }]);
                                                            handleRefresh();
                                                        } catch (error) {
                                                            console.error('Failed to update provider:', error);
                                                        }
                                                    }}
                                                    className="w-full px-4 py-2.5 bg-white border border-neutral-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none appearance-none"
                                                >
                                                    <option value="">Auto-detect (Env Vars)</option>
                                                    <option value="resend">Resend</option>
                                                    <option value="smtp">SMTP</option>
                                                    <option value="console">Console (Development)</option>
                                                </select>
                                                <p className="text-xs text-neutral-500">
                                                    Select which provider to use. Ensure you have the necessary configuration (API keys, Host, etc.) set in the list below or environment variables.
                                                </p>
                                            </div>

                                            <div className="space-y-3 pt-2">
                                                <label className="text-xs font-bold text-neutral-900 uppercase tracking-wide">From Email</label>
                                                <div className="text-sm text-neutral-600 bg-white border border-neutral-200 rounded-lg px-4 py-2.5">
                                                    {providerInfo?.fromEmail || 'Not set'}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Test Email */}
                                    <div className="bg-neutral-50 border border-neutral-100 rounded-xl p-5">
                                        <h3 className="text-sm font-semibold text-neutral-900 mb-4">Test Email Delivery</h3>
                                        <div className="flex items-center gap-3">
                                            <input
                                                type="email"
                                                value={testEmail}
                                                onChange={(e) => setTestEmail(e.target.value)}
                                                placeholder="Enter email address"
                                                className="flex-1 px-4 py-2.5 bg-white border border-neutral-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
                                            />
                                            <button
                                                onClick={handleTestEmail}
                                                disabled={!testEmail || testEmailStatus === 'sending'}
                                                className="px-4 py-2.5 bg-neutral-900 text-white rounded-lg text-sm hover:bg-neutral-800 disabled:opacity-50 flex items-center gap-2"
                                            >
                                                {testEmailStatus === 'sending' ? (
                                                    <RefreshCw size={14} className="animate-spin" />
                                                ) : testEmailStatus === 'success' ? (
                                                    <CheckCircle size={14} className="text-emerald-400" />
                                                ) : testEmailStatus === 'error' ? (
                                                    <XCircle size={14} className="text-red-400" />
                                                ) : (
                                                    <Send size={14} />
                                                )}
                                                {testEmailStatus === 'sending' ? 'Sending...' : 'Send Test'}
                                            </button>
                                        </div>
                                        {testEmailStatus === 'success' && (
                                            <p className="text-xs text-emerald-600 mt-2">Test email sent successfully!</p>
                                        )}
                                        {testEmailStatus === 'error' && (
                                            <p className="text-xs text-red-600 mt-2">Failed to send test email. Check your configuration.</p>
                                        )}
                                    </div>

                                    {/* Configuration Settings */}
                                    <div className="bg-neutral-50 border border-neutral-100 rounded-xl p-5">
                                        <h3 className="text-sm font-semibold text-neutral-900 mb-4">Configuration</h3>
                                        <p className="text-xs text-neutral-500 mb-4">
                                            Email provider settings are configured via environment variables. See the documentation for details.
                                        </p>
                                        <div className="space-y-2">
                                            {configs.length > 0 ? (
                                                configs.map(config => (
                                                    <div key={config.id} className="flex items-center justify-between p-3 bg-white border border-neutral-200 rounded-lg">
                                                        <div>
                                                            <div className="font-mono text-xs text-neutral-900">{config.key}</div>
                                                            {config.description && (
                                                                <div className="text-xs text-neutral-500">{config.description}</div>
                                                            )}
                                                        </div>
                                                        <div className="text-xs text-neutral-600">
                                                            {config.isSecret ? '••••••••' : config.value}
                                                        </div>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="text-sm text-neutral-500">
                                                    No custom configuration stored. Using environment variables.
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* FAILED EXECUTIONS TAB */}
                            {activeTab === 'failed' && (
                                <FailedExecutionsTab
                                    executions={failedExecutions}
                                    pagination={failedPagination}
                                    onLoadData={async (page) => {
                                        try {
                                            const result = await emailApi.listFailedExecutions(page, 20);
                                            setFailedExecutions(result.data);
                                            setFailedPagination({ page: result.pagination.page, total: result.pagination.total, pages: result.pagination.pages });
                                        } catch (error) {
                                            console.error('Failed to load failed executions:', error);
                                        }
                                    }}
                                    onRetry={async (id) => {
                                        try {
                                            await emailApi.retryExecution(id);
                                            // Reload data
                                            const result = await emailApi.listFailedExecutions(failedPagination.page, 20);
                                            setFailedExecutions(result.data);
                                            setFailedPagination({ page: result.pagination.page, total: result.pagination.total, pages: result.pagination.pages });
                                        } catch (error) {
                                            console.error('Failed to retry execution:', error);
                                        }
                                    }}
                                />
                            )}

                            {/* UNSUBSCRIBED TAB */}
                            {activeTab === 'unsubscribed' && (
                                <UnsubscribedTab
                                    emails={unsubscribedEmails}
                                    pagination={unsubPagination}
                                    onLoadData={async (page) => {
                                        try {
                                            const result = await emailApi.listUnsubscribed(page, 20);
                                            setUnsubscribedEmails(result.data);
                                            setUnsubPagination({ page: result.pagination.page, total: result.pagination.total, pages: result.pagination.pages });
                                        } catch (error) {
                                            console.error('Failed to load unsubscribed emails:', error);
                                        }
                                    }}
                                    onResubscribe={async (email) => {
                                        if (!confirm(`Are you sure you want to resubscribe ${email}?`)) return;
                                        try {
                                            await emailApi.resubscribe(email);
                                            // Reload data
                                            const result = await emailApi.listUnsubscribed(unsubPagination.page, 20);
                                            setUnsubscribedEmails(result.data);
                                            setUnsubPagination({ page: result.pagination.page, total: result.pagination.total, pages: result.pagination.pages });
                                        } catch (error) {
                                            console.error('Failed to resubscribe:', error);
                                        }
                                    }}
                                />
                            )}
                        </>
                    )
                    }
                </div >
            </div >

            {/* Template Preview Modal */}
            < TemplatePreviewModal
                isOpen={showPreviewModal}
                onClose={() => {
                    setShowPreviewModal(false);
                    setPreviewTemplate(null);
                }}
                templateId={previewTemplate?.id}
                templateName={previewTemplate?.name}
                htmlContent={previewTemplate?.htmlContent}
                subject={previewTemplate?.subject}
            />

            {/* Create Flow Modal */}
            {
                showCreateFlow && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
                            <h2 className="text-lg font-semibold text-neutral-900 mb-4">Create Email Flow</h2>
                            <div className="space-y-4">
                                <div>
                                    <label className="text-xs font-bold text-neutral-900 uppercase tracking-wide block mb-1">Flow Name</label>
                                    <input
                                        type="text"
                                        value={newFlowName}
                                        onChange={(e) => setNewFlowName(e.target.value)}
                                        placeholder="e.g., Welcome Series"
                                        className="w-full px-4 py-2.5 bg-white border border-neutral-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-neutral-900 uppercase tracking-wide block mb-1">Trigger Event</label>
                                    <select
                                        value={newFlowTrigger}
                                        onChange={(e) => setNewFlowTrigger(e.target.value)}
                                        className="w-full px-4 py-2.5 bg-white border border-neutral-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
                                    >
                                        <option value="">Select a trigger...</option>
                                        {eventTypes.map(et => (
                                            <option key={et.type} value={et.type}>{et.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-neutral-900 uppercase tracking-wide block mb-1">Description (optional)</label>
                                    <textarea
                                        value={newFlowDescription}
                                        onChange={(e) => setNewFlowDescription(e.target.value)}
                                        placeholder="Describe what this flow does..."
                                        rows={2}
                                        className="w-full px-4 py-2.5 bg-white border border-neutral-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none resize-none"
                                    />
                                </div>
                            </div>
                            <div className="flex items-center justify-end gap-3 mt-6">
                                <button
                                    onClick={() => setShowCreateFlow(false)}
                                    className="px-4 py-2 text-sm text-neutral-600 hover:bg-neutral-100 rounded-lg"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleCreateFlow}
                                    disabled={!newFlowName || !newFlowTrigger}
                                    className="px-4 py-2 text-sm bg-neutral-900 text-white rounded-lg hover:bg-neutral-800 disabled:opacity-50"
                                >
                                    Create Flow
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Create Template Modal */}
            {
                showCreateTemplate && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
                            <h2 className="text-lg font-semibold text-neutral-900 mb-4">Create Email Template</h2>
                            <div className="space-y-4">
                                <div>
                                    <label className="text-xs font-bold text-neutral-900 uppercase tracking-wide block mb-1">Template Name</label>
                                    <input
                                        type="text"
                                        value={newTemplateName}
                                        onChange={(e) => setNewTemplateName(e.target.value)}
                                        placeholder="e.g., Welcome Email"
                                        className="w-full px-4 py-2.5 bg-white border border-neutral-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-neutral-900 uppercase tracking-wide block mb-1">Subject Line</label>
                                    <input
                                        type="text"
                                        value={newTemplateSubject}
                                        onChange={(e) => setNewTemplateSubject(e.target.value)}
                                        placeholder="e.g., Welcome to {{appName}}!"
                                        className="w-full px-4 py-2.5 bg-white border border-neutral-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-neutral-900 uppercase tracking-wide block mb-1">Email Content</label>
                                    <RichTextEditor
                                        value={newTemplateContent}
                                        onChange={setNewTemplateContent}
                                        placeholder="Write your email content here..."
                                        minHeight={200}
                                    />
                                </div>
                            </div>
                            <div className="flex items-center justify-end gap-3 mt-6">
                                <button
                                    onClick={() => setShowCreateTemplate(false)}
                                    className="px-4 py-2 text-sm text-neutral-600 hover:bg-neutral-100 rounded-lg"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleCreateTemplate}
                                    disabled={!newTemplateName || !newTemplateSubject || !newTemplateContent}
                                    className="px-4 py-2 text-sm bg-neutral-900 text-white rounded-lg hover:bg-neutral-800 disabled:opacity-50"
                                >
                                    Create Template
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Reset Modal */}
            {showResetModal && <ConfirmResetModal />}
        </div >
    );
};
